import React from 'react'
import UseAnimations from 'react-useanimations'
import loadingAnimation from 'react-useanimations/lib/loading'
import { Flex } from '@mprolab/uikit'
import isNil from 'lodash/isNil'
import isEmpty from 'lodash/isEmpty'
import { useTranslation } from 'react-i18next'
import MainWrapper from 'layouts/MainWrapper'
import AppSessionRequiredPanel from 'components/AppSessionRequiredPanel'
import { EmptyRewards } from 'views/Rewards/components'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useIsUserVerified } from 'state/user/hooks'
import { useLifetimeTokens } from 'state/global/hooks'
import { useGetLicensesQuery } from 'state/services/indexNode/indexNode'

/**
 * Governance is for licence holders: a wallet session plus at least one
 * licence. Applied once here and wrapped around every /dao route, so the list,
 * the detail view and the proposal form cannot drift apart — a gate that
 * covers one of three entrances is not a gate.
 *
 * Uses the same two checks as the rewards page, so the application has one
 * notion of "may see this" rather than two.
 */
const DaoAccessGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation()
  const { account } = useWeb3React()
  const isAppVerified = useIsUserVerified()
  const lifetimeTokens = useLifetimeTokens()
  const accessToken = lifetimeTokens?.[account]

  const { data: licenses, isLoading } = useGetLicensesQuery(
    { account, accessToken },
    { skip: isNil(accessToken) || !isAppVerified }
  )

  if (!account || !isAppVerified) {
    return (
      <MainWrapper>
        <Flex fullWidth mt="152px" mb="120px">
          <AppSessionRequiredPanel />
        </Flex>
      </MainWrapper>
    )
  }

  if (isLoading) {
    // A spinner rather than an empty page: "still checking" and "you may not be
    // here" must not look the same.
    return (
      <MainWrapper>
        <Flex
          fullWidth
          justifyContent="center"
          alignItems="center"
          minHeight={300}
          mt="152px"
          mb="120px"
        >
          <UseAnimations animation={loadingAnimation} strokeColor="#8f8f8f" size={48} />
        </Flex>
      </MainWrapper>
    )
  }

  if (isEmpty(licenses)) {
    return (
      <MainWrapper>
        <Flex fullWidth mt="152px" mb="120px">
          <EmptyRewards
          title={t('dao.licenceRequiredTitle')}
          description={t('dao.licenceRequiredDescription')}
          buttonText={t('rewards.getALicense')}
            redirectTo="/user-licenses"
          />
        </Flex>
      </MainWrapper>
    )
  }

  return <>{children}</>
}

export default DaoAccessGate
